import { useEffect, useMemo, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import Card from "../../components/common/Card";
import Button from "../../components/common/Button";
import Input from "../../components/common/Input";
import Textarea from "../../components/common/Textarea";
import Select from "../../components/common/Select";
import Checkbox from "../../components/common/Checkbox";
import MultiSelect from "../../components/common/MultiSelect";
import Alert from "../../components/common/Alert";
import Modal from "../../components/common/Modal";
import PageHeader from "../../components/common/PageHeader";
import Section from "../../components/common/Section";
import Table from "../../components/common/Table";
import ActionButton from "../../components/common/ActionButton";
import { useStore } from "../../context/StoreContext";
import { getFullImageUrl } from "../../utils/imageUrl";
import {
  useGetItemsQuery,
  useCreateItemMutation,
  useUpdateItemMutation,
  useDeleteItemMutation,
  useGetCategoriesQuery,
  useGetAllergensQuery,
  useGetProteinTypesQuery,
  useGetModifierGroupsQuery,
} from "../../store/api";

const initialFormState = {
  name: "",
  description: "",
  price: "",
  categoryId: "",
  isAvailable: true,
  isTaxable: false,
  availableInDineIn: true,
  availableInTakeaway: true,
  isVeg: false,
  isVegetarian: false,
  isVegan: false,
  isSpicy: false,
  spiceLevel: 0,
  containsAlcohol: false,
  isHalal: false,
  isKosher: false,
  tagIds: [],
  allergenIds: [],
  proteinTypeIds: [],
  modifierGroupIds: [],
};

const EMPTY_GUID = "00000000-0000-0000-0000-000000000000";
const CATEGORY_REQUIRED_MESSAGE = "Please select item category";

const mapAllergenIdsFromItem = (item) => {
  if (Array.isArray(item?.allergenIds)) {
    return item.allergenIds;
  }
  if (!Array.isArray(item?.allergens)) {
    return [];
  }
  return item.allergens
    .map((allergen) => allergen?.allergenId || allergen?.id)
    .filter(Boolean);
};

const mapModifierGroupIdsFromItem = (item) => {
  if (Array.isArray(item?.modifierGroupIds)) {
    return item.modifierGroupIds;
  }
  if (!Array.isArray(item?.modifierGroups)) {
    return [];
  }
  return item.modifierGroups
    .map((group) => group?.groupId || group?.modifierGroupId || group?.id)
    .filter(Boolean);
};

const Items = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { selectedStoreId } = useStore();
  const {
    data: items,
    isLoading,
    isError,
    error,
  } = useGetItemsQuery(selectedStoreId, {
    skip: !selectedStoreId,
  });
  const { data: categories } = useGetCategoriesQuery(selectedStoreId, {
    skip: !selectedStoreId,
  });
  const { data: allergens } = useGetAllergensQuery();
  const { data: proteinTypes } = useGetProteinTypesQuery();
  const { data: modifierGroups } = useGetModifierGroupsQuery(selectedStoreId, {
    skip: !selectedStoreId,
  });

  const [createItem, createState] = useCreateItemMutation();
  const [updateItem, updateState] = useUpdateItemMutation();
  const [deleteItem] = useDeleteItemMutation();

  const [form, setForm] = useState(initialFormState);
  const [editingId, setEditingId] = useState(null);
  const [showForm, setShowForm] = useState(false);
  const [searchQuery, setSearchQuery] = useState("");
  const [imageFile, setImageFile] = useState(null);
  const [imagePreview, setImagePreview] = useState(null);
  const [removeImage, setRemoveImage] = useState(false);
  const [deleteModal, setDeleteModal] = useState({
    isOpen: false,
    itemId: null,
    itemName: "",
  });
  const [errorModal, setErrorModal] = useState({ isOpen: false, message: "" });
  const [successMessage, setSuccessMessage] = useState("");
  const [categoryError, setCategoryError] = useState("");
  const [orderTypeAvailabilityError, setOrderTypeAvailabilityError] =
    useState("");

  const itemsList = Array.isArray(items) ? items : [];
  const categoriesList = Array.isArray(categories) ? categories : [];
  const allergensList = Array.isArray(allergens) ? allergens : [];
  const proteinTypesList = Array.isArray(proteinTypes) ? proteinTypes : [];
  const modifierGroupsList = Array.isArray(modifierGroups)
    ? modifierGroups
    : [];
  const selectedCategoryId = searchParams.get("categoryId") || "";
  const source = searchParams.get("from") || "";
  const fromCategories = source === "categories";
  const selectedCategory = selectedCategoryId
    ? categoriesList.find(
        (category) =>
          String(category.categoryId || category.id) === selectedCategoryId,
      )
    : null;
  const visibleItemsList = selectedCategoryId
    ? itemsList.filter(
        (item) => String(item.categoryId || "") === selectedCategoryId,
      )
    : itemsList;
  const filteredItemsList = useMemo(() => {
    const query = searchQuery.trim().toLowerCase();
    if (!query) {
      return visibleItemsList;
    }
    return visibleItemsList.filter((item) => {
      const haystack =
        `${item.name || item.itemName || ""} ${item.description || ""}`
          .trim()
          .toLowerCase();
      return haystack.includes(query);
    });
  }, [searchQuery, visibleItemsList]);
  const handleCategoryFilterChange = (e) => {
    const nextCategoryId = e.target.value;
    if (!nextCategoryId) {
      if (fromCategories) {
        navigate("/menu/items?from=categories");
      } else {
        navigate("/menu/items");
      }
      return;
    }
    const nextPath = fromCategories
      ? `/menu/items?categoryId=${encodeURIComponent(nextCategoryId)}&from=categories`
      : `/menu/items?categoryId=${encodeURIComponent(nextCategoryId)}`;
    navigate(nextPath);
  };

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    if (name === "categoryId" && value) {
      setCategoryError("");
    }
    if (
      name === "availableInDineIn" ||
      name === "availableInTakeaway"
    ) {
      setOrderTypeAvailabilityError("");
    }
    setForm((prev) => ({
      ...prev,
      [name]: type === "checkbox" ? checked : value,
    }));
  };

  const openAddItemForm = () => {
    resetMutationFeedback();
    setEditingId(null);
    setForm({
      ...initialFormState,
      // Prefill category only when arriving from Categories context.
      categoryId: fromCategories ? selectedCategoryId : "",
    });
    setCategoryError("");
    setImageFile(null);
    setImagePreview(null);
    setRemoveImage(false);
    setShowForm(true);
  };

  const resetMutationFeedback = () => {
    if (typeof createState.reset === "function") {
      createState.reset();
    }
    if (typeof updateState.reset === "function") {
      updateState.reset();
    }
    setSuccessMessage("");
  };

  useEffect(() => {
    if (!successMessage) {
      return undefined;
    }
    const timer = setTimeout(() => {
      setSuccessMessage("");
    }, 3000);
    return () => clearTimeout(timer);
  }, [successMessage]);

  const handleImageChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      if (!file.type.match(/image\/(png|jpg|jpeg)/)) {
        setErrorModal({
          isOpen: true,
          message: "Only PNG and JPG images are allowed",
        });
        return;
      }
      if (file.size > 2 * 1024 * 1024) {
        setErrorModal({
          isOpen: true,
          message: "File size must be less than 2MB",
        });
        return;
      }
      setImageFile(file);
      setRemoveImage(false);
      const reader = new FileReader();
      reader.onloadend = () => {
        setImagePreview(reader.result);
      };
      reader.readAsDataURL(file);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!selectedStoreId) {
      setErrorModal({ isOpen: true, message: "Please select a store first" });
      return;
    }
    resetMutationFeedback();
    const isEditing = Boolean(editingId);
    if (!form.categoryId) {
      setCategoryError(CATEGORY_REQUIRED_MESSAGE);
    }
    if (!form.availableInDineIn && !form.availableInTakeaway) {
      setOrderTypeAvailabilityError(
        "Item must be available in at least one order type (dine-in or takeaway).",
      );
      return;
    }
    try {
      const payload = {
        ...form,
        categoryId: form.categoryId || EMPTY_GUID,
        basePrice: Number(form.price),
        spiceLevel: Number(form.spiceLevel),
        allergens: form.allergenIds.map((allergenId) => ({
          allergenId,
          note: null,
        })),
        modifierGroups: form.modifierGroupIds.map((groupId, index) => ({
          groupId,
          sort: index,
        })),
      };
      delete payload.allergenIds;
      delete payload.modifierGroupIds;
      if (isEditing) {
        await updateItem({
          storeId: selectedStoreId,
          itemId: editingId,
          payload,
          file: imageFile,
          removeImage,
        }).unwrap();
        setEditingId(null);
      } else {
        await createItem({
          storeId: selectedStoreId,
          payload,
          file: imageFile,
        }).unwrap();
      }
      setSuccessMessage(
        `Item ${isEditing ? "updated" : "created"} successfully.`,
      );
      setForm(initialFormState);
      setCategoryError("");
      setOrderTypeAvailabilityError("");
      setShowForm(false);
      setImageFile(null);
      setImagePreview(null);
      setRemoveImage(false);
    } catch (err) {
      // error handled by UI
    }
  };

  const handleEdit = (item) => {
    resetMutationFeedback();
    setEditingId(item.itemId || item.id);
    setForm({
      name: item.name || item.itemName || "",
      description: item.description || "",
      price: item.basePrice || item.price || "",
      categoryId: item.categoryId || "",
      isAvailable:
        typeof item.isAvailable === "boolean" ? item.isAvailable : true,
      isTaxable:
        typeof item.isTaxable === "boolean"
          ? item.isTaxable
          : typeof item.is_taxable === "boolean"
            ? item.is_taxable
            : false,
      availableInDineIn:
        typeof item.availableInDineIn === "boolean"
          ? item.availableInDineIn
          : true,
      availableInTakeaway:
        typeof item.availableInTakeaway === "boolean"
          ? item.availableInTakeaway
          : true,
      isVeg: item.isVeg || false,
      isVegetarian: item.isVegetarian || false,
      isVegan: item.isVegan || false,
      isSpicy: item.isSpicy || false,
      spiceLevel: item.spiceLevel || 0,
      containsAlcohol: item.containsAlcohol || false,
      isHalal: item.isHalal || false,
      isKosher: item.isKosher || false,
      tagIds: item.tagIds || [],
      allergenIds: mapAllergenIdsFromItem(item),
      proteinTypeIds: item.proteinTypeIds || [],
      modifierGroupIds: mapModifierGroupIdsFromItem(item),
    });
    setImagePreview(getFullImageUrl(item.imageUrl));
    setImageFile(null);
    setRemoveImage(false);
    setCategoryError("");
    setOrderTypeAvailabilityError("");
    setShowForm(true);
  };

  const handleDelete = async (itemId) => {
    const item = itemsList.find((i) => (i.itemId || i.id) === itemId);
    setDeleteModal({
      isOpen: true,
      itemId,
      itemName: item?.name || item?.itemName || "this item",
    });
  };

  const confirmDelete = async () => {
    try {
      await deleteItem({
        storeId: selectedStoreId,
        itemId: deleteModal.itemId,
      }).unwrap();
      setDeleteModal({ isOpen: false, itemId: null, itemName: "" });
    } catch (err) {
      setDeleteModal({ isOpen: false, itemId: null, itemName: "" });
      const errorMessage =
        err?.data?.detail ||
        err?.data?.message ||
        "Failed to delete item. It may be used in bundles or promotions.";
      setErrorModal({ isOpen: true, message: errorMessage });
    }
  };

  const handleCancel = () => {
    resetMutationFeedback();
    setEditingId(null);
    setForm(initialFormState);
    setCategoryError("");
    setOrderTypeAvailabilityError("");
    setShowForm(false);
    setImageFile(null);
    setImagePreview(null);
    setRemoveImage(false);
  };

  const columns = [
    { key: "item", label: "Item" },
    { key: "category", label: "Category" },
    { key: "price", label: "Price" },
    { key: "actions", label: "Actions" },
  ];

  const rows = filteredItemsList.map((item) => {
    const id = item.itemId || item.id;
    const category = categoriesList.find(
      (c) => (c.categoryId || c.id) === item.categoryId,
    );
    const imageSrc = getFullImageUrl(item.imageUrl);
    const price = Number(item.basePrice || item.price || 0);

    return {
      id,
      item: (
        <div className="flex items-center gap-3">
          {imageSrc ? (
            <img
              src={imageSrc}
              alt={item.name || item.itemName || "Item image"}
              className="h-11 w-11 rounded-lg border border-slate-200 object-cover"
            />
          ) : (
            <div className="flex h-11 w-11 items-center justify-center rounded-lg border border-slate-200 bg-slate-50">
              <svg
                className="h-5 w-5 text-slate-400"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z"
                />
              </svg>
            </div>
          )}
          <div className="min-w-0">
            <p className="truncate font-semibold text-slate-900">
              {item.name || item.itemName}
            </p>
            {item.description && (
              <p className="truncate text-sm text-slate-500">
                {item.description}
              </p>
            )}
          </div>
        </div>
      ),
      category: category ? category.name || category.categoryName : "-",
      price: (
        <div>
          <p className="font-semibold text-slate-900">${price.toFixed(2)}</p>
          {item.isTaxable ? (
            <p className="text-xs text-slate-500"></p>
          ) : (
            <p className="text-xs text-slate-400"></p>
          )}
        </div>
      ),
      actions: (
        <div className="flex gap-3">
          <ActionButton
            label="Edit"
            icon="edit"
            onClick={() => handleEdit(item)}
          />
          <ActionButton
            label="Delete"
            icon="delete"
            tone="danger"
            onClick={() => handleDelete(id)}
          />
        </div>
      ),
    };
  });

  const mutationError = createState.error?.data || updateState.error?.data;
  const mutationErrorDetail = mutationError?.detail;
  const isCategoryRequiredError =
    mutationError?.code === "CAT_REQUIRED" ||
    (typeof mutationErrorDetail === "string" &&
      mutationErrorDetail.toLowerCase().includes("categoryid is required"));
  const isOrderTypeRequiredError =
    mutationError?.code === "ITEM_ORDER_TYPE_REQUIRED";
  const mutationErrorMessage = isCategoryRequiredError
    ? CATEGORY_REQUIRED_MESSAGE
    : isOrderTypeRequiredError
      ? mutationErrorDetail ||
        "Item must be available in at least one order type (dine-in or takeaway)."
      : mutationErrorDetail || "Failed to save item.";

  if (!selectedStoreId) {
    return (
      <div className="space-y-6">
        <PageHeader title="Items" subtitle="Manage menu items." />
        <Card>
          <Alert tone="warning">Please select a store to manage items.</Alert>
        </Card>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <PageHeader
        title="Items"
        subtitle={
          selectedCategory
            ? `Showing items in ${selectedCategory.name || selectedCategory.categoryName}.`
            : "Manage your menu items with detailed metadata."
        }
        actions={
          showForm ? (
            <Button size="sm" variant="secondary" onClick={handleCancel}>
              Back
            </Button>
          ) : (
            <div className="flex gap-2">
              {selectedCategoryId && (
                <Button
                  size="sm"
                  variant="secondary"
                  onClick={() =>
                    navigate(
                      fromCategories ? "/menu/categories" : "/menu/items",
                    )
                  }
                >
                  Back
                </Button>
              )}
              <Button size="sm" onClick={openAddItemForm}>
                Add Item
              </Button>
            </div>
          )
        }
      />
      {showForm && (
        <Card>
          <h2 className="mb-6 text-lg font-semibold text-slate-900">
            {editingId ? "Edit Item" : "Add Item"}
          </h2>
          <form className="space-y-6" onSubmit={handleSubmit}>
            {/* Image Upload Section - Top Priority */}
            <div className="grid gap-6 lg:grid-cols-3">
              <div className="lg:col-span-1">
                <div className="rounded-lg border border-slate-200 p-4">
                  <label className="mb-3 block text-sm font-medium text-slate-700">
                    Item Image
                  </label>
                  <div className="flex flex-col items-center gap-3">
                    <div className="flex-shrink-0">
                      {imagePreview ? (
                        <img
                          src={imagePreview}
                          alt="Item preview"
                          className="h-40 w-40 rounded-lg border border-slate-200 object-cover"
                        />
                      ) : (
                        <div className="flex h-40 w-40 items-center justify-center rounded-lg border-2 border-dashed border-slate-300 bg-slate-50">
                          <svg
                            className="h-10 w-10 text-slate-400"
                            fill="none"
                            stroke="currentColor"
                            viewBox="0 0 24 24"
                          >
                            <path
                              strokeLinecap="round"
                              strokeLinejoin="round"
                              strokeWidth={2}
                              d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z"
                            />
                          </svg>
                        </div>
                      )}
                    </div>
                    <div className="w-full text-center">
                      <p className="mb-3 text-xs text-slate-600">
                        PNG or JPG, max 2MB
                      </p>
                      <div className="flex flex-col gap-2">
                        <label className="cursor-pointer">
                          <input
                            type="file"
                            accept="image/png,image/jpeg,image/jpg"
                            onChange={handleImageChange}
                            className="hidden"
                          />
                          <span className="inline-flex h-9 items-center rounded-lg border border-slate-200 bg-white px-3 text-sm font-semibold text-slate-700 transition hover:border-slate-300">
                            Choose File
                          </span>
                        </label>
                        {imagePreview && !imageFile && editingId && (
                          <Button
                            type="button"
                            size="sm"
                            variant="secondary"
                            onClick={() => {
                              setImagePreview(null);
                              setRemoveImage(true);
                            }}
                          >
                            Remove
                          </Button>
                        )}
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              {/* Basic Information - Right Side */}
              <div className="lg:col-span-2">
                <Section title="Basic Information">
                  <div className="grid gap-4 md:grid-cols-2">
                    <Input
                      label="Item Name"
                      name="name"
                      placeholder="Margherita Pizza"
                      value={form.name}
                      onChange={handleChange}
                      required
                    />
                    <Input
                      label="Price"
                      name="price"
                      type="text"
                      inputMode="decimal"
                      enterKeyHint="done"
                      pattern="[0-9]*[.]?[0-9]*"
                      placeholder="12.99"
                      value={form.price}
                      onChange={handleChange}
                      required
                    />
                  </div>
                  <Textarea
                    label="Description"
                    name="description"
                    placeholder="Fresh mozzarella, tomato sauce, and basil"
                    value={form.description}
                    onChange={handleChange}
                  />
                  <Select
                    label="Category"
                    name="categoryId"
                    value={form.categoryId}
                    onChange={handleChange}
                    error={categoryError}
                  >
                    <option value="">Select a category</option>
                    {categoriesList.map((cat) => (
                      <option
                        key={cat.categoryId || cat.id}
                        value={cat.categoryId || cat.id}
                      >
                        {cat.name || cat.categoryName}
                      </option>
                    ))}
                  </Select>
                  <Checkbox
                    label="Available in Ordering"
                    name="isAvailable"
                    checked={form.isAvailable}
                    onChange={handleChange}
                  />
                  <Checkbox
                    label="Taxable"
                    name="isTaxable"
                    checked={form.isTaxable}
                    onChange={handleChange}
                    helper="If enabled, Edge applies store tax rates to this item."
                  />
                  <div className="border-t border-slate-200 pt-4">
                    <div className="text-sm font-semibold text-slate-900">
                      Order Type Eligibility (POS)
                    </div>
                    <p className="mt-1 text-xs text-slate-600">
                      Controls whether this item can be sold for dine-in and/or takeaway.
                    </p>
                    <div className="mt-4 grid gap-3 md:grid-cols-2">
                      <Checkbox
                        label="Available in Dine-In"
                        name="availableInDineIn"
                        checked={form.availableInDineIn}
                        onChange={handleChange}
                      />
                      <Checkbox
                        label="Available in Takeaway"
                        name="availableInTakeaway"
                        checked={form.availableInTakeaway}
                        onChange={handleChange}
                      />
                    </div>
                    {orderTypeAvailabilityError && (
                      <div className="mt-4">
                        <Alert tone="error">{orderTypeAvailabilityError}</Alert>
                      </div>
                    )}
                  </div>
                </Section>
              </div>
            </div>

            {/* Dietary & Tags Section */}
            <div className="grid gap-6 lg:grid-cols-2">
              <Section title="Dietary Information & Modifiers">
                <div className="space-y-4">
                  <div className="grid gap-3 md:grid-cols-2">
                    {/* <Checkbox
                      label="Vegetarian"
                      name="isVeg"
                      checked={form.isVeg}
                      onChange={handleChange}
                    /> */}
                    <Checkbox
                      label="Vegan"
                      name="isVegan"
                      checked={form.isVegan}
                      onChange={handleChange}
                    />
                    <Checkbox
                      label="Contains Alcohol"
                      name="containsAlcohol"
                      checked={form.containsAlcohol}
                      onChange={handleChange}
                    />
                    <Checkbox
                      label="Halal"
                      name="isHalal"
                      checked={form.isHalal}
                      onChange={handleChange}
                    />
                    <Checkbox
                      label="Kosher"
                      name="isKosher"
                      checked={form.isKosher}
                      onChange={handleChange}
                    />
                    <Checkbox
                      label="Spicy"
                      name="isSpicy"
                      checked={form.isSpicy}
                      onChange={handleChange}
                    />
                  </div>
                  {form.isSpicy && (
                    <Input
                      label="Spice Level"
                      name="spiceLevel"
                      type="text"
                      inputMode="numeric"
                      enterKeyHint="done"
                      pattern="[0-9]*"
                      placeholder="1-5"
                      value={form.spiceLevel}
                      onChange={handleChange}
                      helper="1 = Mild, 5 = Very Hot"
                    />
                  )}
                  <div className="border-t border-slate-200 pt-4">
                    <MultiSelect
                      label="Attach Modifier Groups"
                      options={modifierGroupsList.map((g) => ({
                        id: g.modifierGroupId || g.id,
                        name: g.name || g.modifierGroupName,
                      }))}
                      selected={form.modifierGroupIds}
                      onChange={(ids) =>
                        setForm((prev) => ({ ...prev, modifierGroupIds: ids }))
                      }
                      helper="Select which modifier groups apply to this item"
                    />
                  </div>
                </div>
              </Section>

              <Section title="Allergens">
                <div className="space-y-4">
                  {/* <MultiSelect
                    label="Tags"
                    options={tagsList.map((t) => ({
                      id: t.tagId || t.id,
                      name: t.name || t.tagName,
                    }))}
                    selected={form.tagIds}
                    onChange={(ids) =>
                      setForm((prev) => ({ ...prev, tagIds: ids }))
                    }
                  /> */}
                  <MultiSelect
                    label="Allergens"
                    options={allergensList.map((a) => ({
                      id: a.allergenId || a.id,
                      name: a.name || a.allergenName,
                    }))}
                    selected={form.allergenIds}
                    onChange={(ids) =>
                      setForm((prev) => ({ ...prev, allergenIds: ids }))
                    }
                  />
                  <MultiSelect
                    label="Protein Types"
                    options={proteinTypesList.map((p) => ({
                      id: p.proteinTypeId || p.id,
                      name: p.name || p.proteinTypeName,
                    }))}
                    selected={form.proteinTypeIds}
                    onChange={(ids) =>
                      setForm((prev) => ({ ...prev, proteinTypeIds: ids }))
                    }
                  />
                </div>
              </Section>
            </div>

            {successMessage && <Alert tone="success">{successMessage}</Alert>}
            {(createState.isError || updateState.isError) && (
              <Alert tone="error">{mutationErrorMessage}</Alert>
            )}

            <div className="flex gap-3">
              <Button
                type="submit"
                disabled={createState.isLoading || updateState.isLoading}
              >
                {editingId ? "Update" : "Add"} Item
              </Button>
              <Button type="button" variant="secondary" onClick={handleCancel}>
                Cancel
              </Button>
            </div>
          </form>
        </Card>
      )}

      {!showForm && (
        <Card className="p-0">
          <div className="border-b border-slate-100 px-6 py-4 text-sm font-semibold text-slate-700">
            Items
          </div>
          <div className="px-6 py-4">
            {isLoading && (
              <p className="text-sm text-slate-500">Loading items...</p>
            )}
            {isError && (
              <Alert tone="error">
                {error?.data?.detail || "Failed to load items."}
              </Alert>
            )}
            {!isLoading && !isError && (
              <>
                <div className="mb-4 flex flex-col gap-3 border-b border-slate-100 pb-4 md:flex-row md:items-end md:justify-between">
                  <div className="grid w-full gap-3 md:grid-cols-2">
                    <Input
                      label="Search by Name"
                      placeholder="Search items..."
                      value={searchQuery}
                      onChange={(e) => setSearchQuery(e.target.value)}
                    />
                    <Select
                      label="Filter by Category"
                      value={selectedCategoryId}
                      onChange={handleCategoryFilterChange}
                    >
                      <option value="">All Categories</option>
                      {categoriesList.map((category) => (
                        <option
                          key={category.categoryId || category.id}
                          value={category.categoryId || category.id}
                        >
                          {category.name || category.categoryName}
                        </option>
                      ))}
                    </Select>
                  </div>
                  <p className="text-sm text-slate-500">
                    Showing {filteredItemsList.length} of {itemsList.length}{" "}
                    items
                  </p>
                </div>
                <Table
                  columns={columns}
                  rows={rows}
                  emptyText={
                    selectedCategory
                      ? "No items found in this category."
                      : "No items yet."
                  }
                />
              </>
            )}
          </div>
        </Card>
      )}

      {/* Delete Confirmation Modal */}
      <Modal
        isOpen={deleteModal.isOpen}
        onClose={() =>
          setDeleteModal({ isOpen: false, itemId: null, itemName: "" })
        }
        title="Delete Item"
        confirmText="Delete"
        confirmTone="danger"
        onConfirm={confirmDelete}
      >
        <p>
          Are you sure you want to delete{" "}
          <strong>{deleteModal.itemName}</strong>?
        </p>
        <p className="mt-2 text-sm text-slate-600">
          This action cannot be undone. The item cannot be deleted if it's used
          in bundles or promotions.
        </p>
      </Modal>

      {/* Error Modal */}
      <Modal
        isOpen={errorModal.isOpen}
        onClose={() => setErrorModal({ isOpen: false, message: "" })}
        title="Error"
        confirmText="OK"
        onConfirm={() => setErrorModal({ isOpen: false, message: "" })}
      >
        <p>{errorModal.message}</p>
      </Modal>
    </div>
  );
};

export default Items;
